import { useFragment } from '@apollo/client';
import { Alert, Box, Button, Stack, styled } from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import {
  memo,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useResizable } from 'react-resizable-layout';
import {
  BlockContainer,
  BlockContent,
  BlockHeader,
  BorderVerticalSeparator,
  CollapsingLayoutBlock,
  DynamicIcon,
  EntityOpenModeSwitcher,
  SidePanelTabs,
  Tooltip,
} from 'src/components';
import { ClientMenu } from 'src/components/ClientComponents/client-menu';
import { ClientName } from 'src/components/ClientComponents/client-name';
import { ExpandedConversations } from 'src/components/EntityFullPageTabs/expanded-conversations';
import { ExpandedSites } from 'src/components/EntityFullPageTabs/expanded-sites';
import { DrawerCloseControl } from 'src/components/Navbar/DesktopDrawer';
import { CLIENT_TABS, ClientTabs } from 'src/components/SidePanelTabs/ClientTab/client-const';
import { ViewerListStateful } from 'src/components/ViewerListStateful/viewer-list-stateful';
import { gql } from 'src/gql';
import { isClientDeleted, useGroupRules, useRestoreClient, UserGroupRules } from 'src/graphql';
import { Client } from 'src/graphql/entities/client/client.types';
import { RecordOpenMode } from 'src/graphql/entities/user-setting-value/user-setting-value.types';
import { EntityNames } from 'src/graphql/types';
import {
  RIGHT_PANEL_MAX_WIDTH_PX,
  RIGHT_PANEL_MIN_WIDTH_PX,
  RIGHT_PANEL_WIDTH_PX,
} from 'src/pages/Conversations/conversation';
import { GridBreadcrumbItem } from 'src/pages/CustomViews/custom-views-selected-view/base-selected-view';
import useClientViewsStore from 'src/store/client-views';
import { Modals, useModalMethods } from 'src/store/modals';
import { useCopyToClipboard, useCustomView } from 'src/utils/hooks';
import { AppEvents, emitAppEvent, subscribeToAppEvent } from 'src/utils/hooks/use-app-events';
import { TabType, useGlobalTabs } from 'src/utils/hooks/use-global-tabs';
import { useNavigateRoute } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';
import { ClientActivityView } from './client-activity-view/client-activity-view';
import { ClientSidePanel } from './SidePanel/side-panel';

const OPEN_DETAILS_PANEL_TOOLTIP = 'Open Client Details';
const CLOSE_DETAILS_PANEL_TOOLTIP = 'Close Client Details';

const EXPANDED_SITES_VIEW_ID = 'clientSites';

const ClientContainer = styled(Stack, {
  name: 'ClientContainer',
})(({ theme }) => ({
  position: 'relative',
  flexDirection: 'row',
  flex: 1,
  zIndex: theme.zIndex.app.ServiceDeskConversation,
  width: 0,
}));

const ClientDetailsWrapper = styled(CollapsingLayoutBlock, {
  name: 'ClientDetailsWrapper',
})(({ theme, open }) => ({
  position: 'static',
  [theme.breakpoints.down('sm')]: {
    position: 'absolute',
    top: 0,
    height: '100%',
    right: -1,
    ...(!open ? {} : { right: 1 }),
  },
}));

const SidePanelTabsInnerStyles = {
  padding: 0,
};

const SidePanelTabStyles = {
  fontSize: '0.75rem',
};

const TabContentFragment = gql(`
  fragment ClientWindowTabContent_Client on Client {
    id
    clientId
    partnerId
    deletedAt
  }
`);

interface TabContentProps {
  clientId: string;
  activeTab: ClientTabs;
}

const TabContent = ({ activeTab, clientId }: TabContentProps) => {
  const { data: client, complete } = useFragment({
    fragment: TabContentFragment,
    fragmentName: 'ClientWindowTabContent_Client',
    from: {
      __typename: 'Client',
      id: clientId,
    },
  });

  const { showModal: showUpdateClientToAddressModal } = useModalMethods(
    Modals.UpdateClientToAddress,
  );

  const conversationsViewFilters = useMemo(
    () => [
      {
        attribute: 'requester/participantIdentity',
        operator: '=',
        value: clientId,
        logicalOperator: 'or',
      },
      {
        attribute: 'associatedAddresses/clients/clientId',
        operator: '=',
        value: clientId,
        logicalOperator: 'or',
      },
    ],
    [clientId],
  );

  const sitesViewFilters = useMemo(
    () => [
      {
        attribute: 'primaryClientId',
        operator: '=',
        value: clientId,
        logicalOperator: 'or',
      },
      {
        attribute: 'clients/clientId',
        operator: '=',
        value: clientId,
        logicalOperator: 'or',
      },
    ],
    [clientId],
  );

  useEffect(
    () =>
      subscribeToAppEvent(AppEvents.ClientsToAddressAssociation, ({ clientIds }) => {
        if (!clientId || !clientIds.includes(clientId)) return;
        emitAppEvent(AppEvents.SearchRecordsInvalidated, {
          entityName: EntityNames.Site,
          viewId: EXPANDED_SITES_VIEW_ID,
        });
      }),
    [clientId],
  );

  const onAddSiteClicked = useCallback(() => {
    showUpdateClientToAddressModal({
      // TODO(gql-migrate): UpdateClientToAddressModal still consumes the legacy Client
      // shape; it only reads clientId/partnerId, which this fragment selects.
      // TODO(gql-migrate): ^^ it is wrong, UpdateClientToAddressModal passes entire Client object down. Investigate this.
      client: client as Client,
    });
  }, [client, showUpdateClientToAddressModal]);

  if (!complete) {
    return null;
  }

  switch (true) {
    case activeTab === ClientTabs.Sites:
      return (
        <ExpandedSites
          viewFilters={sitesViewFilters}
          customViewType={EntityNames.Client}
          viewId={EXPANDED_SITES_VIEW_ID}
          createButton={
            <Button onClick={onAddSiteClicked} disabled={isClientDeleted(client)}>
              + Add Site
            </Button>
          }
        />
      );

    case activeTab === ClientTabs.Conversations:
      return (
        <ExpandedConversations
          viewFilters={conversationsViewFilters}
          customViewType={EntityNames.Client}
          viewId="clientConversations"
        />
      );

    case activeTab === ClientTabs.Activity:
      return <ClientActivityView clientId={clientId} partnerId={client.partnerId} />;

    default:
      return null;
  }
};

const ClientWindowFragment = gql(`
  fragment ClientWindow_Client on Client {
    id
    clientId
    partnerId
    firstName
    lastName
    fullName @client
    deletedAt
    ...ClientWindowTabContent_Client
    ...ClientSidePanel_Client
    ...ClientMenu_Client
  }
`);

export const ClientWindowComponent = ({
  isSidePanelExpanded,
  clientId,
  onCloseClient,
}: {
  isSidePanelExpanded: boolean;
  clientId: string;
  onCloseClient: () => void;
}) => {
  const { data: client, complete } = useFragment({
    fragment: ClientWindowFragment,
    fragmentName: 'ClientWindow_Client',
    from: {
      __typename: 'Client',
      id: clientId,
    },
  });
  const { navigate } = useNavigateRoute();
  const { isMobile, isDesktop } = useScreenDimensions();
  const {
    selectedClientTab,
    setSelectedClientTab,
    clientSidePanelStateOpen,
    setClientSidePanelStateOpen,
  } = useClientViewsStore();

  const customView = useCustomView(EntityNames.Client);
  const customViewId = customView?.customViewId;

  useEffect(() => {
    if (isMobile) {
      setClientSidePanelStateOpen(false);
    }
  }, [isMobile, setClientSidePanelStateOpen]);

  const canResizeDetailsView = clientSidePanelStateOpen && isDesktop;

  const isResizableSplitMode = isSidePanelExpanded && canResizeDetailsView;

  const clientViewRef = useRef<HTMLDivElement | null>(null);

  const { closeTabWith } = useGlobalTabs();

  const closeTab = () => {
    closeTabWith(
      (tab) => tab.type === TabType.Client && tab.entity?.id === clientId,
      false,
    );
  };

  const {
    position: sidePanelWidth,
    separatorProps,
    isDragging,
  } = useResizable({
    axis: 'x',
    initial: RIGHT_PANEL_WIDTH_PX,
    min: RIGHT_PANEL_MIN_WIDTH_PX,
    max: RIGHT_PANEL_MAX_WIDTH_PX,
    containerRef: clientViewRef as RefObject<HTMLElement>,
    reverse: true,
    disabled: !clientSidePanelStateOpen || isMobile,
  });

  const closeSidePanel = useCallback(
    () => setClientSidePanelStateOpen(false),
    [setClientSidePanelStateOpen],
  );

  const { copyToClipboard } = useCopyToClipboard();

  const { checkValidity, loading: loadingGroupRules } = useGroupRules();

  const { restoreClient } = useRestoreClient();

  const onRestoreClientClick = useCallback(async () => {
    await restoreClient(clientId);
  }, [restoreClient, clientId]);

  const tabs = useMemo(() => {
    return CLIENT_TABS.filter(
      (tab) =>
        !tab.allowedRules ||
        (!loadingGroupRules && checkValidity(tab.allowedRules, { matchType: 'any' })),
    );
  }, [checkValidity, loadingGroupRules]);

  if (!complete) {
    return null;
  }

  return (
    <ClientContainer
      ref={clientViewRef}
      sx={{
        userSelect: isDragging ? 'none' : 'auto',
      }}
    >
      <BlockContainer leftRoundedOnly={isResizableSplitMode} sx={{ minWidth: '20rem' }}>
        <BlockHeader>
          <DrawerCloseControl />
          {!isMobile && isSidePanelExpanded ? (
            <GridBreadcrumbItem handleClick={onCloseClient} text={customView?.name || 'All'} />
          ) : null}
          <ClientName
            fullName={client.fullName}
            tooltipText="Click to copy"
            onClick={() => {
              if (client.fullName) {
                void copyToClipboard(client.fullName);
              }
            }}
          />
          <Box flexGrow={1} />
          <ViewerListStateful entity="Client" entityId={clientId} />
          {!isMobile && (
            <EntityOpenModeSwitcher
              openMode={RecordOpenMode.SidePeek}
              onClick={() => {
                closeTab();
                navigate(
                  portalPaths.entity.all({
                    entityName: 'clients',
                    id: clientId,
                    customViewId,
                  }),
                );
              }}
            />
          )}
          <ClientMenu clientId={clientId} />
          <Tooltip
            title={
              clientSidePanelStateOpen
                ? CLOSE_DETAILS_PANEL_TOOLTIP
                : OPEN_DETAILS_PANEL_TOOLTIP
            }
          >
            <DynamicIcon
              name={clientSidePanelStateOpen ? 'right_panel_close' : 'right_panel_open'}
              sx={{ cursor: 'pointer' }}
              onClick={() => setClientSidePanelStateOpen(!clientSidePanelStateOpen)}
            />
          </Tooltip>
        </BlockHeader>
        <BlockContent
          sx={{
            p: 0,
            justifyContent: 'end',
            position: 'static',
          }}
        >
          <Stack flexGrow={1} justifyContent="space-between" overflow="auto">
            <Box
              sx={{
                position: 'relative',
                width:
                  !isMobile && clientSidePanelStateOpen && !isSidePanelExpanded
                    ? `calc(100% - ${Math.max(sidePanelWidth, 0)}px)`
                    : '100%',
                height: '100%',
              }}
            >
              <SidePanelTabs
                showLabel
                key={clientId}
                activeTab={selectedClientTab}
                setActiveTab={(newValue: SetStateAction<ClientTabs>) => {
                  setSelectedClientTab(newValue as ClientTabs);
                }}
                tabs={tabs}
                variant="scrollable"
                tabSx={SidePanelTabStyles}
                innerSx={SidePanelTabsInnerStyles}
                hasBorderTop={false}
              >
                <Stack flexGrow={1} minHeight={0}>
                  <TabContent activeTab={selectedClientTab} clientId={clientId} />
                </Stack>
              </SidePanelTabs>
            </Box>

            {!isSidePanelExpanded ? (
              <ClientDetailsWrapper
                open={clientSidePanelStateOpen}
                sx={{
                  width: isMobile ? '100%' : sidePanelWidth,
                  ...(isDragging && { transition: 'none' }),
                  zIndex: isMobile ? 1 : undefined,
                }}
              >
                <ClientSidePanel onClose={closeSidePanel} clientId={clientId} extendedView />
              </ClientDetailsWrapper>
            ) : null}
          </Stack>
          {isClientDeleted(client) ? (
            <Alert
              severity="info"
              action={
                checkValidity([UserGroupRules.AccessToRestoreClients]) ? (
                  <Button color="inherit" size="small" onClick={onRestoreClientClick}>
                    RESTORE
                  </Button>
                ) : null
              }
            >
              This client was deleted
            </Alert>
          ) : null}
        </BlockContent>
      </BlockContainer>

      {isResizableSplitMode ? <BorderVerticalSeparator {...separatorProps} /> : null}

      {isSidePanelExpanded ? (
        <ClientDetailsWrapper
          open={clientSidePanelStateOpen}
          sx={{
            width: isMobile ? '100%' : sidePanelWidth,
            ...(isDragging && { transition: 'none' }),
            zIndex: 1,
            maxHeight: 'calc(100vh - 15px)',
          }}
        >
          <ClientSidePanel onClose={closeSidePanel} clientId={clientId} extendedView />
        </ClientDetailsWrapper>
      ) : null}
    </ClientContainer>
  );
};

export const ClientWindow = memo(ClientWindowComponent);
